import React from 'react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';

export default function Legal({ variant }) {
  const isPrivacy = variant === 'privacy';
  const effectiveDate = 'February 2026';

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <PublicNav />

      <article className="max-w-3xl mx-auto px-6 py-16 leading-relaxed">
        <p className="text-[10px] uppercase tracking-wider text-[#D4A843] font-bold mb-2">Legal</p>
        <h1 className="text-4xl font-bold mb-2">{isPrivacy ? 'Privacy Policy' : 'Terms of Service'}</h1>
        <p className="text-sm text-gray-500 mb-12">Effective {effectiveDate} · EIGOO Inc., Hagåtña, Guam USA</p>

        <div className="prose prose-invert max-w-none text-gray-300 space-y-5 text-sm">
          {isPrivacy ? <PrivacyBody /> : <TermsBody />}
        </div>

        <p className="text-[11px] text-gray-500 mt-16 border-t border-gray-800 pt-6">
          Questions about this {isPrivacy ? 'privacy policy' : 'agreement'}? Email us at <a href="mailto:sales@eigooinc.net" className="text-[#D4A843] underline">sales@eigooinc.net</a>.
        </p>
      </article>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-bold text-[#D4A843] mb-3">{title}</h2>
      {children}
    </section>
  );
}

function PrivacyBody() {
  return (
    <>
      <p>EIGOO Inc. ("we," "us," "EIGOO") operates techops.eigooinc.net and related services (the "Platform"). This Privacy Policy explains what we collect, how we use it, and the choices you have.</p>

      <Section title="1. Information We Collect">
        <p><strong className="text-white">You provide:</strong> account details (name, email, password hash), billing information (processed by Stripe), application content (tickets, diagnostic reports, uploaded files), and communications with our team.</p>
        <p><strong className="text-white">Automatic:</strong> IP address, user-agent, session activity, device diagnostic telemetry you submit, and standard server logs.</p>
        <p><strong className="text-white">Third parties:</strong> OAuth providers (Google), payment processors, analytics providers, and LLM providers (OpenAI, Google) where features require them. We never sell your data.</p>
      </Section>

      <Section title="2. How We Use It">
        <p>To operate the Platform, provide customer support, improve our services, send transactional and occasional marketing emails (with opt-out), enforce our Terms, and comply with law. Freelancer and partner agreements are retained for seven years as required for contractor records.</p>
      </Section>

      <Section title="3. Sharing">
        <p>We share data only with: (a) subprocessors who run parts of our infrastructure (hosting, email, payments, LLM inference) under written data protection terms; (b) your freelancers or partners with your explicit consent; and (c) law enforcement when legally compelled.</p>
      </Section>

      <Section title="4. Security">
        <p>We use encrypted transport (TLS 1.2+), hashed passwords (bcrypt), httpOnly session cookies, access logging, and role-based permissions. Payment data is handled by Stripe and never stored on our servers. No system is perfectly secure — if we learn of a breach affecting you, we will notify you within 72 hours.</p>
      </Section>

      <Section title="5. Your Rights">
        <p>You may request access to, correction of, export of, or deletion of your data by emailing <a href="mailto:sales@eigooinc.net" className="text-[#D4A843] underline">sales@eigooinc.net</a>. We respond within 30 days. EU / UK residents have GDPR rights; California residents have CCPA/CPRA rights. Residents of the territory of Guam have equivalent rights under Guam consumer privacy rules.</p>
      </Section>

      <Section title="6. Cookies">
        <p>We use essential cookies for authentication and session state. Analytics and marketing cookies are optional and gated by a cookie banner where applicable.</p>
      </Section>

      <Section title="7. Children">
        <p>The Platform is not directed to children under 16 and we do not knowingly collect data from them.</p>
      </Section>

      <Section title="8. Changes">
        <p>We may update this Policy. Material changes will be announced by email and on this page with a revised effective date. Continued use after changes constitutes acceptance.</p>
      </Section>
    </>
  );
}

function TermsBody() {
  return (
    <>
      <p>These Terms of Service ("Terms") govern your access to and use of techops.eigooinc.net and related services (the "Platform") operated by EIGOO Inc. ("we," "us"). By signing up or otherwise using the Platform you agree to be bound by these Terms.</p>

      <Section title="1. Accounts">
        <p>You must be at least 18 and authorized to bind your organization. Keep your credentials confidential; you are responsible for activity under your account.</p>
      </Section>

      <Section title="2. Subscriptions & Billing">
        <p>Paid plans renew automatically at the interval selected. Fees are non-refundable except where required by law or our separately published refund policy. You may cancel at any time from your account; cancellation takes effect at the end of the current billing period.</p>
      </Section>

      <Section title="3. Acceptable Use">
        <p>You agree not to (a) use the Platform to violate any law, (b) probe, scan, or attempt to breach our systems, (c) upload malware or infringing content, (d) impersonate anyone, or (e) resell the Platform without a written reseller agreement.</p>
      </Section>

      <Section title="4. Content You Submit">
        <p>You retain ownership of content you upload (tickets, documents, telemetry). You grant us a limited license to host, process, and display it solely to provide the Platform. We will not use your data to train third-party models without your consent.</p>
      </Section>

      <Section title="5. Marketplace & Freelancers">
        <p>The Platform connects you with independent freelancers and service providers. We are not a party to the engagement between you and any freelancer except where explicitly stated. Freelancers operate under our separate Independent Contractor Agreement.</p>
      </Section>

      <Section title="6. Disclaimers">
        <p>THE PLATFORM IS PROVIDED "AS IS." WE DISCLAIM ALL WARRANTIES TO THE MAXIMUM EXTENT PERMITTED BY LAW, INCLUDING FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. CYBERSECURITY DIAGNOSTICS AND RECOMMENDATIONS ARE INFORMATIONAL AND DO NOT GUARANTEE A SECURE OUTCOME.</p>
      </Section>

      <Section title="7. Limitation of Liability">
        <p>OUR TOTAL LIABILITY IN ANY 12-MONTH PERIOD WILL NOT EXCEED THE FEES YOU PAID IN THAT PERIOD. WE WILL NOT BE LIABLE FOR INDIRECT, CONSEQUENTIAL, OR PUNITIVE DAMAGES. SOME JURISDICTIONS DO NOT ALLOW THESE LIMITATIONS; THEY APPLY TO THE MAXIMUM EXTENT PERMITTED.</p>
      </Section>

      <Section title="8. Termination">
        <p>We may suspend or terminate your access for material breach of these Terms. You may close your account at any time. Sections that by their nature should survive (IP, liability, governing law) survive termination.</p>
      </Section>

      <Section title="9. Governing Law">
        <p>These Terms are governed by the laws of the Territory of Guam, without regard to conflict-of-laws principles. Disputes are resolved by binding arbitration in Hagåtña, Guam under American Arbitration Association Commercial Rules, except either party may bring small-claims actions in their local court.</p>
      </Section>

      <Section title="10. Changes">
        <p>We may update these Terms. Material changes will be announced by email and posted here with a revised effective date. Continued use after changes constitutes acceptance.</p>
      </Section>
    </>
  );
}
